import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePost } from "../../../api-client/usePost";
import { useForm } from "../../../hooks/useForm";
import { DealerOrderPreview } from "../../../queries/usePreviewDealerOrderFromCart";
import { useDealerAddress } from "../../../stores/hooks/useDealerAddress";
import { useNullableDealerAccountInfo } from "../../../stores/hooks/useNullableDealerAccountInfo";
import { PrimaryButton } from "../../../ui/Button/Button";
import { Input } from "../../../ui/Input/Input";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { phoneE164Schema } from "../../../util/phoneSchema";
import { StripeKeys } from "./StripePaymentForm";
import { StripeWrapper } from "./StripeWrapper";
import { NormalizedDealerOrder } from "./useNormalizedDealerOrder";
import { useTotals } from "./useTotals";

type DealerOrderFormProps = {
  data: DealerOrderPreview;
  hide: VoidFunction;
  normalizedData: NormalizedDealerOrder[];
  revalidate: () => Promise<void>;
};

const schema = z.object({
  customerEmail: z.string().email(),
  customerPhone: phoneE164Schema("CA"),
  firstName: z.string().min(2),
  lastName: z.string().min(2),
});

export const BuyNowForm: FunctionComponent<DealerOrderFormProps> = ({
  data,
  hide,
  normalizedData,
  revalidate,
}) => {
  const dealerAddress = useDealerAddress();
  const dealer = useNullableDealerAccountInfo();
  const { t } = useTranslation();

  const {
    data: stripeKeys,
    reset,
    send: createPaymentIntent,
  } = usePost<StripeKeys>("/stripe/create-payment-intent");

  const isAvailable = normalizedData.every((item) => item.orderEnabled);

  const { subtotal } = useTotals(normalizedData);

  const { send: saveCart } = usePost<DealerOrderPreview>(
    "/retailSiteWebService/shopping/cart/saveCart.php"
  );

  const [handleForm, { busy }] = useForm({
    noReset: true,
    async onSubmit({ customerEmail, customerPhone, firstName, lastName }) {
      await Promise.all([
        saveCart({
          cartId: data.orderNumber,
          customerContactMethodPreference: data.customerContactMethodPreference,
          customerEmail,
          customerName: [firstName, lastName].filter(Boolean).join(" "),
          customerPhone,
          dealerId: dealer?.dealerId ?? "",
          notes: "",
          orderNumber: data.orderNumber,
        }),
        createPaymentIntent({
          orderId: data.orderId,
        }),
      ]);
      await revalidate();
    },
    schema,
  });

  const formId = `order-form-${data.orderNumber}`;

  const [firstName, ...lastName] = data.customerName?.split(" ") || [];

  return (
    <section
      className={clsx(
        "p-4 bg-paper-2 border-t border-dashed border-edge-2 gap-4",
        "grid gap-8 lg:grid-cols-3 xl:grid-cols-4"
      )}
    >
      <div className="flex flex-col md:p-4 md:self-stretch lg:col-span-2 xl:col-span-3">
        <h3 className="font-semibold">{dealer?.dealerName}</h3>
        <p className="text-sm text-ink-2">{dealerAddress}</p>
        <hr className="border-edge-2 my-3" />
        <form
          className={clsx(
            "grid gap-2 md:gap-4",
            "md:grid-cols-2 xl:grid-cols-4"
          )}
          id={formId}
          noValidate
          onSubmit={handleForm}
        >
          <h3 className="font-semibold md:col-span-2 xl:col-span-4">
            {t("dealerOrderModal.contactInformation")}
          </h3>
          <label className="grid gap-1">
            <span className="text-sm text-ink-2">
              {t("dealerOrderModal.firstName")}:
            </span>
            <Input
              aria-label="yourFirstName"
              autoComplete="given-name"
              className="bg-paper border-edge-2"
              defaultValue={firstName}
              id="do-po"
              name="firstName"
              placeholder={t("dealerOrderModal.firstNamePlaceholder")}
              type="text"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-ink-2">
              {t("dealerOrderModal.lastName")}:
            </span>
            <Input
              aria-label="yourLastName"
              autoComplete="family-name"
              className="bg-paper border-edge-2"
              defaultValue={lastName?.join(" ")}
              id="do-po"
              name="lastName"
              placeholder={t("dealerOrderModal.lastNamePlaceholder")}
              type="text"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-ink-2">
              {t("dealerOrderModal.phone")}:
            </span>
            <Input
              aria-label="yourPhone"
              autoComplete="tel"
              className="bg-paper border-edge-2"
              defaultValue={data.customerPhone}
              id="do-po"
              inputMode="tel"
              name="customerPhone"
              placeholder={t("dealerOrderModal.phonePlaceholder")}
              type="text"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-ink-2">
              {t("dealerOrderModal.email")}:
            </span>
            <Input
              aria-label="yourEmail"
              autoComplete="email"
              className="bg-paper border-edge-2"
              defaultValue={data.customerEmail}
              id="do-po"
              name="customerEmail"
              placeholder={t("dealerOrderModal.emailPlaceholder")}
              type="text"
            />
          </label>
        </form>
      </div>
      <div
        className={clsx(
          "border border-edge-2 p-4 md:p-6 rounded-md",
          "flex flex-col h-full w-full justify-center"
        )}
      >
        <KeyValue className="justify-between py-2 md:py-3 border-b-edge-2 border-b mb-2 md:mb-6">
          <>{t("dealerOrderModal.total")}:</>
          <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
            {subtotal}
          </PriceDisplay>
        </KeyValue>
        {!isAvailable && (
          <div className="text-sm text-error mb-6">
            {t("buyNowModal.nonAvailableMessage")}
          </div>
        )}
        {stripeKeys ? (
          <StripeWrapper
            onComplete={async (): Promise<void> => {
              hide();
              await revalidate();
            }}
            clientSecret={stripeKeys.clientSecret}
            customerEmail={data.customerEmail}
            onCancel={reset}
            publishableKey={stripeKeys.publishableKey}
          />
        ) : (
          <PrimaryButton
            aria-label="placeOrder"
            busy={busy}
            className="h-12 mt-auto"
            disabled={!isAvailable}
            form={formId}
            type="submit"
          >
            {t("buyNowModal.payNow")}
          </PrimaryButton>
        )}
      </div>
    </section>
  );
};
